import { AppName } from '@components/AppName';
import { Logo } from '@components/Logo';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useAuth } from '@hooks/useAuth';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { useIsWeb } from '@hooks/useIsWeb';
import { AddReportCreationModeModal } from '@screens/ReportCreation/components/CsvImport/AddReportCreationModeModal';
import { AddReportCsvImportModal } from '@screens/ReportCreation/components/CsvImport/AddReportCsvImportModal';
import { SupportedLanguage, supportedLanguages } from '@store/i18n';
import { canAccessTab } from '@utils/userPermissions';
import { useRouter, useSegments } from 'expo-router';
import React, { useState } from 'react';
import { Pressable } from 'react-native';
import { SizableText, useTheme, XStack, YStack } from 'tamagui';
import { getMainTabs, notificationsTab, profileTab } from '../navBar.constants';
import { isTabName, TabItem, TabName } from '../navBar.types';
import { topNavBarHeight } from './topNavBar.constants';
import { Indicator } from '@components/Indicator/indicator.styled';
import { useUnreadNotifications } from '@hooks/useUnreadNotifications';

const totalPadding = 30;

/**
 * Props for the TopNavBar component.
 */
interface TopNavBarProps {
  height?: number;
  horizontalMargin?: number;
}

/**
 * Top navigation bar component for desktop/web layout.
 */
export const TopNavBar = ({
  height = topNavBarHeight,
  horizontalMargin,
}: TopNavBarProps): React.ReactElement => {
  const router = useRouter();
  const segments = useSegments();
  const { t, language, setLanguage } = useAppTranslation();
  const theme = useTheme();
  const { isSmallScreen, isLargeScreen } = useBreakpoint();
  const isWeb = useIsWeb();
  const { user } = useAuth();
  const [isAddReportModalOpen, setIsAddReportModalOpen] = useState(false);
  const [isCsvImportModalOpen, setIsCsvImportModalOpen] = useState(false);
  const tabs = getMainTabs({ isWeb, isSmallScreen }).filter((item) =>
    canAccessTab(item, user),
  );
  const unreadNotifications = useUnreadNotifications();
  const currentTab = segments.find(isTabName);

  const VerticalSeparator: React.FC = () => (
    <YStack width="$separator" height={30} backgroundColor="$color" />
  );

  const handlePress = (tab: TabName): void => {
    if (tab === TabName.reportCreation) {
      setIsAddReportModalOpen(true);
      return;
    }

    router.push(`/(tabs)/${tab}`);
  };

  const handleInAppFormPress = (): void => {
    setIsAddReportModalOpen(false);
    router.push('/(tabs)/report-creation');
  };

  const handleCsvImportPress = (): void => {
    setIsAddReportModalOpen(false);
    setIsCsvImportModalOpen(true);
  };

  const closeAllAddReportModals = (): void => {
    setIsAddReportModalOpen(false);
    setIsCsvImportModalOpen(false);
  };

  const handleCsvImportBack = (): void => {
    setIsCsvImportModalOpen(false);
    setIsAddReportModalOpen(true);
  };

  return (
    <XStack
      position="fixed"
      top={0}
      left={0}
      right={0}
      height={height}
      backgroundColor="$background"
      borderBottomWidth={1}
      borderBottomColor="$color"
      alignItems="center"
      justifyContent="space-between"
      marginHorizontal={horizontalMargin}
      zIndex="$5"
    >
      <Pressable onPress={() => handlePress(TabName.board)}>
        <XStack alignItems="center" gap="$3">
          <Logo height={height - totalPadding} />

          {isLargeScreen && (
            <>
              <VerticalSeparator />
              <AppName />
            </>
          )}
        </XStack>
      </Pressable>

      <XStack alignItems="center" gap="$3">
        <XStack alignItems="center" gap="$4">
          {tabs.map((item: TabItem) => {
            const isActive = currentTab === item.name;
            return (
              <Pressable key={item.name} onPress={() => handlePress(item.name)}>
                <SizableText
                  size="$7"
                  color={isActive ? '$brand' : '$color'}
                  fontFamily="$heading"
                >
                  {t(item.labelKey)}
                </SizableText>
              </Pressable>
            );
          })}
        </XStack>

        <VerticalSeparator />

        <XStack alignItems="center" gap="$2">
          {supportedLanguages.map((lang: SupportedLanguage) => {
            const isLanguageActive = language === lang;
            return (
              <Pressable key={lang} onPress={() => void setLanguage(lang)}>
                <SizableText
                  color="$color"
                  fontFamily={isLanguageActive ? '$bold' : '$body'}
                  textDecorationLine={isLanguageActive ? 'underline' : 'none'}
                >
                  {lang.toUpperCase()}
                </SizableText>
              </Pressable>
            );
          })}
        </XStack>

        <VerticalSeparator />

        <Pressable onPress={() => handlePress(notificationsTab.name)}>
          {unreadNotifications && <Indicator size={10} />}
          <Ionicons
            name="notifications-outline"
            size={20}
            color={
              currentTab === notificationsTab.name
                ? theme.brand?.val
                : theme.color.val
            }
          />
        </Pressable>

        <Pressable onPress={() => handlePress(profileTab.name)}>
          <Ionicons
            name={profileTab.iconName}
            size={20}
            color={
              currentTab === profileTab.name
                ? theme.brand?.val
                : theme.color.val
            }
          />
        </Pressable>
      </XStack>

      <AddReportCreationModeModal
        isOpen={isAddReportModalOpen}
        onClose={closeAllAddReportModals}
        onSelectInAppForm={handleInAppFormPress}
        onSelectCsvImport={handleCsvImportPress}
      />

      <AddReportCsvImportModal
        isOpen={isCsvImportModalOpen}
        onClose={closeAllAddReportModals}
        onBack={handleCsvImportBack}
      />
    </XStack>
  );
};
